import React, { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import Svg, { Circle, Path } from 'react-native-svg';
import {
  deleteChatAvatar,
  deleteOwnAvatar,
  uploadChatAvatar,
  uploadOwnAvatar,
  type AvatarCropPayload,
} from '../api/client';
import { type AvatarShapeId } from '../avatar/shapeCatalog';
import { AvatarShapeStrip } from './AvatarShapeStrip';
import { useLocale } from '../i18n/LocaleContext';
import { humanizeError } from '../util/humanizeError';
import { chatListPalette } from '../theme/chatListPalette';
import { useVisualTheme } from '../theme/VisualThemeContext';
import { MAX_ANIMATED_AVATAR_BYTES, isGifSource } from '../util/animatedAvatarSource';
import { hapticSelection } from '../util/haptics';
import { pickAvatarPhoto, type AvatarPickSource, type PickedAvatarPhoto } from '../util/pickAvatarPhoto';
import { processAvatarForUpload, releaseProcessedImageUpload } from '../util/processImageForSend';
import { AvatarBadge } from './AvatarBadge';
import { AvatarPhotoEditor } from './AvatarPhotoEditor';

/** Куда уходит снимок: свой профиль или конкретный чат. */
type AvatarEditorTarget = { kind: 'user'; userId: string } | { kind: 'chat'; chatId: string };

type Props = {
  target: AvatarEditorTarget;
  displayName: string;
  hasAvatar: boolean;
  revision: number;
  shapeId: AvatarShapeId;
  /** Заголовок редактора кадра («Фото профиля» / «Фото чата»). */
  title: string;
  /** Права на правку. Без них панель — просто аватар, без единой кнопки. */
  canEdit?: boolean;
  /** Задан — форму можно менять; у чата она наследуется и меняться не может. */
  onShapeChange?: (id: AvatarShapeId) => void;
  onUploaded: (revision: number) => void;
  onRemoved: (revision: number) => void;
  /** Тап по аватару, когда правки нет: обычно открыть карточку. */
  onPreview?: () => void;
  badgeSize?: number;
};

type Mode = 'idle' | 'choosing' | 'editing' | 'confirmRemove';

/**
 * Фото профиля и фото чата — одна панель.
 *
 * <p><b>Что было.</b> Две копии одного и того же (`AvatarSettingsSection` и
 * `ChatAvatarEditor`): выбор из галереи, чипы с процентами, `PanResponder` по бейджу, загрузка,
 * удаление — слово в слово, но в двух системах стилей. Каждая находка чинилась дважды или,
 * чаще, в одном месте из двух.
 *
 * <p><b>Что стало.</b> Аватар нажимается сам — как в любом мессенджере, — и открывает выбор
 * источника: снять, выбрать, удалить. Кадр задаётся в `AvatarPhotoEditor` жестами. Список
 * действий нарисован тут же, а не отдельным `BottomSheet`: панель уже живёт внутри модалки
 * (настройки, информация о группе, лист чата), а вторая одновременная системная презентация
 * подвешивает экран на iOS — тем же граблям обязана карточка в `UserAvatarPreviewModal`.
 */
export function AvatarEditorPanel({
  target,
  displayName,
  hasAvatar,
  revision,
  shapeId,
  title,
  canEdit = true,
  onShapeChange,
  onUploaded,
  onRemoved,
  onPreview,
  badgeSize = 120,
}: Props): React.ReactElement {
  const { t } = useLocale();
  const { resolvedVisualTheme } = useVisualTheme();
  const pal = useMemo(() => chatListPalette(resolvedVisualTheme), [resolvedVisualTheme]);

  const [mode, setMode] = useState<Mode>('idle');
  const [picked, setPicked] = useState<PickedAvatarPhoto | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const badgeProps =
    target.kind === 'user' ? { userId: target.userId } : { chatAvatarChatId: target.chatId };

  const onPick = useCallback(
    async (source: AvatarPickSource) => {
      setErr(null);
      const res = await pickAvatarPhoto(source);
      if (res.status === 'cancelled') {
        setMode('idle');
        return;
      }
      if (res.status === 'error') {
        setErr(t(res.messageKey));
        setMode('idle');
        return;
      }
      setPicked(res.photo);
      setMode('editing');
    },
    [t],
  );

  const onApplyCrop = useCallback(
    async (crop: AvatarCropPayload) => {
      if (!picked) return;
      setErr(null);
      setBusy(true);
      try {
        // Живую (анимированную) аватарку отправляем файлом как есть. Через `processAvatarForUpload`
        // она проходит одним кадром — снимок переупаковывается в JPEG, и до сервера доезжает
        // неподвижная картинка: именно это выглядело как «GIF ставится, но не анимируется».
        // Кадрирование анимации делает сервер, покадрово; клиенту тут нечем.
        const animated = await isGifSource(picked.uri, picked.mimeType);
        if (animated && picked.sizeBytes != null && picked.sizeBytes > MAX_ANIMATED_AVATAR_BYTES) {
          setErr(t('avatarAnimatedTooLarge'));
          return;
        }
        // Пережимаем до отправки: сервер принимает не больше 8 МБ и читает только то, что
        // понимает ImageIO (JPEG/PNG), а снимок с камеры Android легко больше (стори 5.3).
        const processed = animated
          ? { uri: picked.uri, mimeType: 'image/gif' }
          : await processAvatarForUpload(picked.uri, picked.width ?? 0, picked.height ?? 0);
        const file = {
          kind: 'uri' as const,
          uri: processed.uri,
          filename: animated ? 'avatar.gif' : 'avatar.jpg',
          mimeType: processed.mimeType,
        };
        let avatarRevision: number;
        try {
          ({ avatarRevision } =
            target.kind === 'user'
              ? await uploadOwnAvatar(file, crop)
              : await uploadChatAvatar(target.chatId, file, crop));
        } finally {
          if (processed.uri !== picked.uri) {
            await releaseProcessedImageUpload({ fullUri: processed.uri });
          }
        }
        setPicked(null);
        setMode('idle');
        onUploaded(avatarRevision);
      } catch (e) {
        setErr(humanizeError(e, t('settingsAvatarUploadFailed')));
      } finally {
        setBusy(false);
      }
    },
    [onUploaded, picked, t, target],
  );

  const onRemove = useCallback(async () => {
    setErr(null);
    setBusy(true);
    try {
      const { avatarRevision } =
        target.kind === 'user' ? await deleteOwnAvatar() : await deleteChatAvatar(target.chatId);
      setPicked(null);
      setMode('idle');
      onRemoved(avatarRevision);
    } catch (e) {
      setErr(humanizeError(e, t('errorGeneric')));
    } finally {
      setBusy(false);
    }
  }, [onRemoved, t, target]);

  if (mode === 'editing' && picked) {
    return (
      <AvatarPhotoEditor
        uri={picked.uri}
        imageWidth={picked.width}
        imageHeight={picked.height}
        title={title}
        shapeId={shapeId}
        onShapeChange={onShapeChange}
        busy={busy}
        onCancel={() => {
          setPicked(null);
          setMode('idle');
        }}
        onApply={(crop) => void onApplyCrop(crop)}
      />
    );
  }

  const badge = (
    <AvatarBadge
      {...badgeProps}
      displayName={displayName}
      revision={revision}
      hasAvatar={hasAvatar}
      shapeId={shapeId}
      size={badgeSize}
    />
  );

  // Без права правки и без карточки нажимать не на что — тогда это просто аватар, а не
  // кнопка, которая ничего не делает.
  if (!canEdit && !onPreview) {
    return (
      <View style={styles.root}>
        <View style={styles.badgeRow}>{badge}</View>
      </View>
    );
  }

  return (
    <View style={styles.root}>
      <View style={styles.badgeRow}>
        <Pressable
          onPress={() => {
            if (!canEdit) {
              onPreview?.();
              return;
            }
            hapticSelection();
            setErr(null);
            setMode(mode === 'choosing' ? 'idle' : 'choosing');
          }}
          disabled={busy}
          accessibilityRole="button"
          accessibilityLabel={canEdit ? t('avatarChangePhoto') : t('userCardTitle')}>
          {badge}
          {canEdit ? (
            <View
              style={[
                styles.cameraBubble,
                { backgroundColor: pal.modalPrimary, borderColor: pal.modalCard },
              ]}
              pointerEvents="none">
              <Svg width={18} height={18} viewBox="0 0 20 20">
                <Path
                  d="M2 5.6h3.4l1.1-1.8h6.9l1.1 1.8H18a1 1 0 0 1 1 1v8.2a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V6.6a1 1 0 0 1 1-1Z"
                  fill="#fff"
                />
                <Circle cx={10} cy={10.8} r={3.1} fill={pal.modalPrimary} />
              </Svg>
            </View>
          ) : null}
        </Pressable>
      </View>

      {canEdit && mode === 'idle' ? (
        <Text style={[styles.hint, { color: pal.settingsFieldLabel }]}>{t('avatarTapToChange')}</Text>
      ) : null}

      {canEdit && mode === 'choosing' ? (
        <View style={[styles.actions, { borderColor: pal.chatFilterChipBorder }]}>
          {/* На вебе «снять» открыл бы тот же файловый диалог — строку не показываем. */}
          {Platform.OS !== 'web' ? (
            <ActionRow
              label={t('avatarTakePhoto')}
              color={pal.modalBtnText}
              divider={pal.chatFilterChipBorder}
              onPress={() => void onPick('camera')}
              disabled={busy}
            />
          ) : null}
          <ActionRow
            label={t('avatarFromLibrary')}
            color={pal.modalBtnText}
            divider={pal.chatFilterChipBorder}
            onPress={() => void onPick('library')}
            disabled={busy}
          />
          {hasAvatar ? (
            <ActionRow
              label={t('settingsAvatarRemove')}
              color={pal.dmErr}
              divider={pal.chatFilterChipBorder}
              onPress={() => setMode('confirmRemove')}
              disabled={busy}
              last
            />
          ) : null}
        </View>
      ) : null}

      {canEdit && mode === 'confirmRemove' ? (
        <View style={[styles.actions, { borderColor: pal.chatFilterChipBorder }]}>
          <Text style={[styles.confirmText, { color: pal.settingsFieldLabel }]}>
            {t('avatarRemoveConfirm')}
          </Text>
          <ActionRow
            label={t('settingsAvatarRemove')}
            color={pal.dmErr}
            divider={pal.chatFilterChipBorder}
            onPress={() => void onRemove()}
            disabled={busy}
          />
          <ActionRow
            label={t('cancel')}
            color={pal.modalBtnText}
            divider={pal.chatFilterChipBorder}
            onPress={() => setMode('choosing')}
            disabled={busy}
            last
          />
        </View>
      ) : null}

      {busy ? <ActivityIndicator color={pal.modalPrimary} /> : null}

      {canEdit ? <AvatarShapeStrip shapeId={shapeId} onShapeChange={onShapeChange} /> : null}

      {err ? <Text style={[styles.err, { color: pal.dmErr }]}>{err}</Text> : null}
    </View>
  );
}

function ActionRow({
  label,
  color,
  divider,
  onPress,
  disabled,
  last,
}: {
  label: string;
  color: string;
  divider: string;
  onPress: () => void;
  disabled?: boolean;
  last?: boolean;
}): React.ReactElement {
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={label}
      style={[
        styles.actionRow,
        last ? null : { borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: divider },
        disabled ? styles.actionRowDisabled : null,
      ]}>
      <Text style={[styles.actionLabel, { color }]}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: { gap: 8 },
  badgeRow: { alignItems: 'center', justifyContent: 'center' },
  /** Пузырь камеры на бейдже — та же подсказка, что и во всех мессенджерах: аватар нажимается. */
  cameraBubble: {
    position: 'absolute',
    right: -2,
    bottom: -2,
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  hint: { fontSize: 12, textAlign: 'center' },
  /** Список действий — сгруппированные строки, как в системных листах. */
  actions: { borderWidth: StyleSheet.hairlineWidth, borderRadius: 12, overflow: 'hidden' },
  actionRow: { minHeight: 48, justifyContent: 'center', paddingHorizontal: 14 },
  actionRowDisabled: { opacity: 0.5 },
  actionLabel: { fontSize: 15, textAlign: 'center' },
  confirmText: { fontSize: 13, textAlign: 'center', paddingHorizontal: 14, paddingVertical: 10 },
  label: { fontSize: 13, fontWeight: '600' },
  shapeStrip: { gap: 8, paddingVertical: 2 },
  shapeCell: { padding: 4, borderRadius: 10, borderWidth: 1, borderColor: 'transparent' },
  err: { fontSize: 13 },
});
